import { Injectable, inject } from '@angular/core';
import { injectQueryClient, mutationOptions, queryOptions } from '@tanstack/angular-query-experimental';
import { BackendService } from './backend.service';
import { Label } from '../types/label';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { AnalyticsService } from './analytics.service';
import { EVENT_CREATED_LABEL } from '../analytics/events';
import { CreateLabelRequest, Label as ApiLabel, LabelPage } from '../generated-api/models';
import { labelQueryKeys } from './query-keys';

export type CreateLabelMutationVariables = {
  name: string;
  color: string;
};

@Injectable({ providedIn: 'root' })
export class LabelService {
  private analytics = inject(AnalyticsService);
  private backend = inject(BackendService);

  private readonly queryClient = injectQueryClient();

  async createLabel(name: string, color: string): Promise<Label> {
    await this.fetchLabels();
    const label = await firstValueFrom(
      this.backend.request<ApiLabel, CreateLabelRequest>({
        type: 'post',
        apiRoute: 'label',
        data: { name, color },
      }),
    );

    this.analytics.sendEvent(EVENT_CREATED_LABEL);
    this.queryClient.setQueryData<Label[]>(labelQueryKeys.list(), labels => [...(labels ?? []), label]);
    void this.queryClient.invalidateQueries({ queryKey: labelQueryKeys.list(), refetchType: 'none' });
    return label;
  }

  createLabelMutationOptions() {
    return mutationOptions({
      mutationKey: ['label', 'create'],
      mutationFn: ({ name, color }: CreateLabelMutationVariables) => this.createLabel(name, color),
    });
  }

  labelListQueryOptions() {
    return queryOptions({
      queryKey: labelQueryKeys.list(),
      queryFn: () => this.fetchLabelsFromBackend(),
      staleTime: 0,
    });
  }

  private fetchLabels(): Promise<Label[]> {
    return this.queryClient.fetchQuery(this.labelListQueryOptions());
  }

  private fetchLabelsFromBackend(): Promise<Label[]> {
    return firstValueFrom(
      this.backend
        .request<LabelPage>({
          type: 'get',
          apiRoute: 'label',
          params: { pageNumber: 1, pageSize: 10000 },
        })
        .pipe(map(page => page.data)),
    );
  }
}
